import React, { useState } from 'react';
import { Card, Form, Input, Button, Modal, Space, Divider, Switch, message, Tabs, Typography } from 'antd';
import { EyeOutlined, SaveOutlined, CodeOutlined, ReloadOutlined } from '@ant-design/icons';
import { DevelopmentLoanAgreement } from './DevelopmentLoanAgreement';

interface LoanAgreementConfig {
  enabled: boolean;
  agreementTitle: string;
  lenderName: string;
  officeDepartment: string;
  dearBorrowerText: string;
  facilityTerms: {
    facilityType: string;
    securityText: string;
    repaymentScheduleText: string;
  };
  electronicAcceptance: {
    borrowerAcceptanceText: string;
    saccosApprovalText: string;
    effectiveTimeText: string;
  };
  conditionsBeforeDisbursement: string[];
  disbursementAndUse: string[];
  interestFeesAndCharges: string[];
  repayment: string[];
  securityAndGuarantees: string[];
  defaultAndRemedies: string[];
  governingLaw: string;
  acceptanceBoxText: string;
  buttonText: string;
  colors: {
    primary: string;
    secondary: string;
    text: string;
    border: string;
    lightBorder: string;
    pageBackground: string;
    labelBackground: string;
    valueBackground: string;
    noticeBackground: string;
    noticeBorder: string;
    acceptanceBackground: string;
    buttonBackground: string;
  };
}

const defaultConfig: LoanAgreementConfig = {
  enabled: false,
  agreementTitle: 'DEVELOPMENT LOAN OFFER AND ELECTRONIC ACCEPTANCE AGREEMENT',
  lenderName: 'CRDB WORKERS SACCOS LTD',
  officeDepartment: 'CRDB SACCOS Processing Unit CREDIT & OPERATION',
  dearBorrowerText: 'CRDB Workers SACCOS Ltd (the "SACCOS" or "Lender") has approved the Development Loan described below for the member identified above (the "Borrower"). This Agreement consists of the Facility Terms, the Standard Terms and Conditions, the Security Schedule, the Repayment Schedule and the Electronic Acceptance Certificate, which together form the complete loan agreement.',
  facilityTerms: {
    facilityType: 'Development Loan',
    securityText: 'As detailed in Schedule 1 - Security (Borrower and Guarantor Savings)',
    repaymentScheduleText: 'As set out in Annex 1 - System-Generated Repayment Schedule',
  },
  electronicAcceptance: {
    borrowerAcceptanceText: 'The Borrower shall accept this Agreement electronically through the CRDB SACCOS member portal or another approved digital channel. No handwritten signature is required. Acceptance is valid only when the Borrower:',
    saccosApprovalText: 'The SACCOS shall approve and issue this Agreement through an authorised system workflow. The system shall record the approving officer\'s identity, role, approval date and time, loan reference and Agreement version. The SACCOS approval record and the Borrower\'s authenticated acceptance together constitute the electronic signatures of both parties. No wet-ink signature blocks are required.',
    effectiveTimeText: 'This Agreement becomes effective when the Borrower\'s authenticated acceptance is successfully recorded against an Agreement already approved and issued by the SACCOS. The Electronic Acceptance Certificate in Schedule 3, together with the audit trail, shall form part of this Agreement and may be used to establish the identity, intention, date, time, version and integrity of the transaction.',
  },
  conditionsBeforeDisbursement: [
    'The Borrower remains an eligible member and all information provided is complete and accurate;',
    'The loan has been approved through the SACCOS credit approval process;',
    'The required borrower savings, guarantor savings and any other approved security have been verified, documented and made available in accordance with the lending policy;',
    'Each guarantor has received the required liability disclosure and has separately accepted the guarantee electronically or in another legally valid form;',
    'The selected deduction instruction has been activated;',
    'All disclosed application and insurance fees have been paid or authorized for deduction; and',
    'Any specific condition stated under clause 1.9 has been fulfilled.',
  ],
  disbursementAndUse: [
    'The SACCOS shall disburse the Facility in the manner shown in clauses 1.7 and 1.12. Any deduction from the gross Facility amount shall be limited to fees and charges expressly disclosed in this Agreement.',
    'The Borrower shall state the Facility in clause 1.4',
    'The Borrower confirms that all statements and documents submitted in connection with the Facility are true, complete and not misleading in any material respect.',
  ],
  interestFeesAndCharges: [
    'Interest shall be calculated using the method and rate stated in clauses 1.17 to 1.19 and reflected in the Repayment Schedule.',
    'The Borrower shall pay only the fees, charges, penalties and recovery expenses expressly stated in this Agreement or subsequently agreed in a valid written or electronic amendment accepted by both parties.',
  ],
  repayment: [
    'The Borrower shall repay the Facility by the instalments and due dates shown in Annex 1. The selected deduction channel under clause 1.16 is a collection method and does not remove the Borrower\'s personal obligation to ensure that every instalment is paid in full and on time.',
    'If an HR/payroll deduction or standing order is delayed, rejected, reduced or stopped, the Borrower shall immediately pay the affected instalment through an alternative channel approved by the SACCOS and shall promptly restore the deduction instruction.',
    'Where a standing order is selected, the Borrower shall authorizes the recurring deduction of the instalment stated in clause 1.10 with service charge of TZS 300 on the agreed deduction date and undertakes to maintain sufficient funds in the nominated account.',
    'The Borrower may repay the Facility early, in whole or in part, on a business day without a prepayment penalty. Interest shall not be charged for the remaining period after full early repayment, subject to settlement of accrued amounts and properly disclosed charges up to the settlement date.',
  ],
  securityAndGuarantees: [
    'The Facility is secured by the savings amounts stated in Schedule 1 - Security. The SACCOS may, at its sole discretion, apply these savings to repay any outstanding obligations under this Agreement if the Borrower defaults.',
    'Each guarantor listed in Schedule 1 is jointly and severally liable for the obligations of the Borrower up to the maximum liability amount specified. The SACCOS may demand payment from any guarantor without first proceeding against the Borrower.',
    'The Borrower and each guarantor irrevocably authorize the SACCOS to deduct from their savings accounts any amounts due under this Agreement without further notice.',
  ],
  defaultAndRemedies: [
    'An Event of Default occurs if the Borrower fails to pay any instalment when due, breaches any term of this Agreement, provides false or misleading information, or becomes insolvent.',
    'Upon an Event of Default, the SACCOS may: (a) demand immediate repayment of the entire outstanding balance; (b) apply security savings to the outstanding debt; (c) enforce guarantees; (d) take legal action; and (e) report the default to credit reference bureaus.',
    'The Borrower shall reimburse the SACCOS for all reasonable costs, expenses and legal fees incurred in enforcing or protecting its rights under this Agreement.',
  ],
  governingLaw: 'This Agreement shall be governed by and construed in accordance with the laws of the United Republic of Tanzania. Any dispute arising under this Agreement shall be subject to the exclusive jurisdiction of the courts of Tanzania.',
  acceptanceBoxText: 'I have read, understood and accept this Development Loan Agreement, including all schedules and disclosures. I intend my authenticated checkbox selection and confirmation to constitute my electronic signature and to bind me to the Agreement.',
  buttonText: 'ACCEPT LOAN OFFER',
  colors: {
    primary: '#2f7047',
    secondary: '#24527f',
    text: '#111111',
    border: '#d9e0e0',
    lightBorder: '#b9c6c0',
    pageBackground: '#ffffff',
    labelBackground: '#eaf2ed',
    valueBackground: '#eaf1f6',
    noticeBackground: '#fff5d6',
    noticeBorder: '#e4c86a',
    acceptanceBackground: '#eaf1f6',
    buttonBackground: '#24527f',
  },
};

interface LoanAgreementConfigPanelProps {
  initialConfig?: string;
  onChange: (config: string) => void;
}

const LoanAgreementConfigPanel: React.FC<LoanAgreementConfigPanelProps> = ({
  initialConfig,
  onChange,
}) => {
  const [form] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [rawJson, setRawJson] = useState('');
  const [rawJsonError, setRawJsonError] = useState<string>();
  const [config, setConfig] = useState<LoanAgreementConfig>(() => {
    if (initialConfig) {
      try {
        return { ...defaultConfig, ...JSON.parse(initialConfig) };
      } catch (e) {
        console.error('Failed to parse initial config:', e);
        return defaultConfig;
      }
    }
    return defaultConfig;
  });

  React.useEffect(() => {
    if (!initialConfig) return;
    try {
      const nextConfig = { ...defaultConfig, ...JSON.parse(initialConfig) };
      setConfig(nextConfig);
      form.setFieldsValue(nextConfig);
      setRawJson(JSON.stringify(nextConfig, null, 2));
      setRawJsonError(undefined);
    } catch (e) {
      console.error('Failed to parse initial config:', e);
    }
  }, [initialConfig, form]);

  React.useEffect(() => {
    setRawJson(JSON.stringify(config, null, 2));
  }, [config]);

  const applyRawJson = () => {
    try {
      const parsed = JSON.parse(rawJson);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Configuration must be a JSON object');
      }
      const normalized = { ...defaultConfig, ...parsed };
      setConfig(normalized);
      form.setFieldsValue(normalized);
      setRawJsonError(undefined);
      message.success('Raw JSON applied');
    } catch (error) {
      setRawJsonError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  };

  const handleSave = () => {
    try {
      const parsed = JSON.parse(rawJson);
      const normalized = { ...defaultConfig, ...parsed };
      setConfig(normalized);
      onChange(JSON.stringify(normalized, null, 2));
      setRawJson(JSON.stringify(normalized, null, 2));
      setRawJsonError(undefined);
      message.success('Loan agreement configuration saved');
    } catch (error) {
      setRawJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      message.error('Fix the raw JSON before saving');
    }
  };

  const handlePreview = () => {
    setPreviewVisible(true);
  };

  const handleArrayChange = (field: keyof LoanAgreementConfig, index: number, value: string) => {
    const arrayField = config[field] as string[];
    const newArray = [...arrayField];
    newArray[index] = value;
    const updatedConfig = { ...config, [field]: newArray };
    setConfig(updatedConfig);
    onChange(JSON.stringify(updatedConfig, null, 2));
  };

  const handleAddArrayItem = (field: keyof LoanAgreementConfig) => {
    const arrayField = config[field] as string[];
    const updatedConfig = { ...config, [field]: [...arrayField, ''] };
    setConfig(updatedConfig);
    onChange(JSON.stringify(updatedConfig, null, 2));
  };

  const handleRemoveArrayItem = (field: keyof LoanAgreementConfig, index: number) => {
    const arrayField = config[field] as string[];
    const newArray = arrayField.filter((_, i) => i !== index);
    const updatedConfig = { ...config, [field]: newArray };
    setConfig(updatedConfig);
    onChange(JSON.stringify(updatedConfig, null, 2));
  };

  return (
    <div>
      <Card
        title="Loan Agreement Configuration"
        extra={
          <Space>
            <Button icon={<EyeOutlined />} onClick={handlePreview}>
              Preview Agreement
            </Button>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>
              Save Configuration
            </Button>
            <Button icon={<CodeOutlined />} onClick={() => setRawJson(JSON.stringify(config, null, 2))}>
              Format JSON
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => {
              setConfig(defaultConfig);
              form.setFieldsValue(defaultConfig);
              setRawJson(JSON.stringify(defaultConfig, null, 2));
              setRawJsonError(undefined);
              onChange(JSON.stringify(defaultConfig, null, 2));
            }}>
              Reset CRDB Defaults
            </Button>
          </Space>
        }
      >
        <Tabs defaultActiveKey="structured">
          <Tabs.TabPane tab="Structured Editor" key="structured">
            <Form
              form={form}
              layout="vertical"
              initialValues={config}
              onValuesChange={(changedValues) => {
                const nextConfig = { ...config, ...changedValues };
                setConfig(nextConfig);
                setRawJson(JSON.stringify(nextConfig, null, 2));
                onChange(JSON.stringify(nextConfig, null, 2));
              }}
            >
              <Form.Item name="enabled" label="Enable Loan Agreement" valuePropName="checked">
                <Switch />
              </Form.Item>

              {config.enabled && (
                <>
                  <Divider orientation="left">Basic Information</Divider>
                  <Form.Item name="agreementTitle" label="Agreement Title">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="lenderName" label="Lender Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="officeDepartment" label="Office / Department">
                    <Input />
                  </Form.Item>

                  <Divider orientation="left">Introduction Text</Divider>
                  <Form.Item name="dearBorrowerText" label="Dear Borrower Text">
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <Divider orientation="left">Facility Terms</Divider>
                  <Form.Item name={['facilityTerms', 'facilityType']} label="Facility Type">
                    <Input />
                  </Form.Item>
                  <Form.Item name={['facilityTerms', 'securityText']} label="Security Text">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name={['facilityTerms', 'repaymentScheduleText']} label="Repayment Schedule Text">
                    <Input.TextArea rows={2} />
                  </Form.Item>

                  <Divider orientation="left">Electronic Acceptance</Divider>
                  <Form.Item name={['electronicAcceptance', 'borrowerAcceptanceText']} label="Borrower Acceptance Text">
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Form.Item name={['electronicAcceptance', 'saccosApprovalText']} label="SACCOS Approval Text">
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Form.Item name={['electronicAcceptance', 'effectiveTimeText']} label="Effective Time Text">
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <Divider orientation="left">Conditions Before Disbursement</Divider>
                  {(config.conditionsBeforeDisbursement as string[]).map((item, index) => (
                    <Space key={index} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        noStyle
                        name={['conditionsBeforeDisbursement', index]}
                      >
                        <Input.TextArea
                          rows={2}
                          style={{ width: 500 }}
                          value={item}
                          onChange={(e) => handleArrayChange('conditionsBeforeDisbursement', index, e.target.value)}
                        />
                      </Form.Item>
                      <Button
                        danger
                        onClick={() => handleRemoveArrayItem('conditionsBeforeDisbursement', index)}
                      >
                        Remove
                      </Button>
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => handleAddArrayItem('conditionsBeforeDisbursement')}
                    block
                  >
                    Add Condition
                  </Button>

                  <Divider orientation="left">Disbursement and Use</Divider>
                  {(config.disbursementAndUse as string[]).map((item, index) => (
                    <Space key={index} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item
                        noStyle
                        name={['disbursementAndUse', index]}
                      >
                        <Input.TextArea
                          rows={2}
                          style={{ width: 500 }}
                          value={item}
                          onChange={(e) => handleArrayChange('disbursementAndUse', index, e.target.value)}
                        />
                      </Form.Item>
                      <Button
                        danger
                        onClick={() => handleRemoveArrayItem('disbursementAndUse', index)}
                      >
                        Remove
                      </Button>
                    </Space>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => handleAddArrayItem('disbursementAndUse')}
                    block
                  >
                    Add Clause
                  </Button>

                  <Divider orientation="left">Acceptance</Divider>
                  <Form.Item name="acceptanceBoxText" label="Acceptance Box Text">
                    <Input.TextArea rows={4} />
                  </Form.Item>
                  <Form.Item name="buttonText" label="Button Text">
                    <Input />
                  </Form.Item>
                </>
              )}
            </Form>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Raw JSON" key="raw">
            <Typography.Paragraph type="secondary">
              Edit the complete persisted configuration. Colours are under <code>colors</code> and accept any valid CSS colour value.
            </Typography.Paragraph>
            <Input.TextArea
              value={rawJson}
              onChange={(event) => {
                setRawJson(event.target.value);
                setRawJsonError(undefined);
              }}
              autoSize={{ minRows: 24, maxRows: 48 }}
              spellCheck={false}
              status={rawJsonError ? 'error' : undefined}
            />
            {rawJsonError && <Typography.Text type="danger">{rawJsonError}</Typography.Text>}
            <Space style={{ marginTop: 12 }}>
              <Button type="primary" onClick={applyRawJson}>Apply JSON to Editor and Preview</Button>
              <Button onClick={() => setRawJson(JSON.stringify(config, null, 2))}>Discard Raw Changes</Button>
            </Space>
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <Modal
        title="Loan Agreement Preview"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <DevelopmentLoanAgreement
            agreementId="AGR-PREVIEW-001"
            offerDate="10/08/2026"
            offerValidFrom="10/08/2026 08:00"
            offerValidUntil="17/08/2026 23:59"
            borrower={{
              name: "John Michael Doe",
              memberNumber: "SAC000123",
              mobileNumber: "07XXXXXXXX",
              email: "john@example.com",
              nida: "1990XXXXXXXXXXXX",
            }}
            loan={{
              reference: "LN-2026-000123",
              amount: 50_000_000,
              existingBalance: 5_000_000,
              totalExposure: 55_000_000,
              purpose: "Construction of residential house",
              tenorMonths: 60,
              disbursementSchedule: "Single disbursement",
              gracePeriod: "3 months",
              specificTerms: "Disbursement subject to verification of required documents.",
              monthlyInstallment: 1_250_000,
              disbursementAccount: "0123456789 - John Michael Doe",
              applicationFee: "TZS 100,000",
              insuranceFee: "TZS 250,000",
              repaymentFrequency: "Monthly",
              deductionMethod: "HR / Payroll Deduction",
              interestRate: 12,
              effectiveAnnualInterestRate: 14.2,
              interestComputationMethod: "Reducing Balance",
              totalAmountPayable: 75_000_000,
              latePaymentPenalty: "5% of overdue instalment",
              debtRecoveryFees: "Actual reasonable costs / approved scale",
            }}
            security={{
              borrowerSavings: 10_000_000,
              guarantors: [
                {
                  fullName: "Jane Michael",
                  memberNumber: "SAC000456",
                  maximumLiability: 20_000_000,
                  mobile: "07XXXXXXXX",
                  acceptanceRecord: "10/08/2026 09:15 | OTP | AUD-12345",
                },
              ],
              totalSecurity: 50_000_000,
            }}
            repaymentSchedule={[
              {
                number: 1,
                dueDate: "30/09/2026",
                openingBalance: 50_000_000,
                principal: 750_000,
                interest: 500_000,
                fees: 0,
                totalInstallment: 1_250_000,
                closingBalance: 49_250_000,
              },
            ]}
            borrowerAcceptance={{
              accepted: true,
              statementVersion: "v1.0",
              authenticationMethod: "OTP",
              acceptedAt: "10/08/2026 09:30:45 EAT",
              sessionAuditId: "AUD-987654",
              ipDeviceInformation: "192.168.xxx.xxx | Android | Chrome",
            }}
            saccosApproval={{
              officerName: "Credit Officer",
              officerUserId: "USR-001",
              officerRole: "Credit Manager",
              approvedAt: "10/08/2026 08:30:00 EAT",
            }}
            certificate={{
              agreementVersion: "1.0",
              documentHash: "SHA256:xxxxxxxxxxxxxxxxxxxxxxxx",
              finalAgreementDelivery: "PORTAL DOWNLOAD / EMAIL",
              certificateStatus: "VALID",
            }}
            agreementConfig={config}
          />
        </div>
      </Modal>
    </div>
  );
};

export default LoanAgreementConfigPanel;